'use client'

import { motion, AnimatePresence } from 'framer-motion'

export interface Update {
    date: string
    label: string
    title: string
    description: string
    items?: string[]
}

interface UpdatesContentProps {
    updates: Update[]
    selectedDate: string
}

export function UpdatesContent({ updates, selectedDate }: UpdatesContentProps) {
    const selectedUpdate = updates.find((u) => u.date === selectedDate)

    return (
        <div className="min-h-[500px]">
            <AnimatePresence mode="wait">
                {selectedUpdate && (
                    <motion.div
                        key={selectedDate}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{
                            duration: 0.3,
                            ease: 'easeInOut',
                        }}
                        className="space-y-6"
                    >
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black-6 text-black-80 rounded-full text-xs font-medium border border-black-6">
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
                                        fill="currentColor"
                                        stroke="currentColor"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {selectedUpdate.label}
                            </span>
                        </div>

                        <div>
                            <h2 className="text-[28px] leading-[34px] font-semibold text-black-100 mb-3">
                                {selectedUpdate.title}
                            </h2>
                            <p className="text-black-60 text-base leading-relaxed">
                                {selectedUpdate.description}
                            </p>
                        </div>

                        {selectedUpdate.items && selectedUpdate.items.length > 0 && (
                            <div className="space-y-3">
                                {selectedUpdate.items.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-start gap-3"
                                    >
                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-black-40 flex-shrink-0" />
                                        <p className="text-black-80 text-sm leading-relaxed">
                                            {item}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        <div className="pt-6 border-t border-black-6">
                            <a
                                href="#"
                                className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                Learn more
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M6 12L10 8L6 4"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
